'use client';

import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback } from 'react';

import { useAuth } from '@/hooks/use-auth';
import { readErrorEnvelope } from '@/lib/http-error';

export type AiUsageRemaining = {
  usageCount: number;
  limit: number | null;
  remaining: number | null;
  /** ledger = 組織共通の回数枠（ADR-0044）。月次の上限を持たず、remaining が null なら無制限 */
  creditMode: 'legacy' | 'ledger';
  nearestExpiresAt: string | null;
};

export type UseAiUsageRemainingResult = {
  usageCount: number | null;
  limit: number | null;
  remaining: number | null;
  isUnlimited: boolean;
  creditMode: 'legacy' | 'ledger';
  nearestExpiresAt: string | null;
  loading: boolean;
  isError: boolean;
  hasOrganization: boolean;
};

export const AI_USAGE_REMAINING_QUERY_KEY = ['ai-usage', 'current'] as const;
const AI_USAGE_FETCH_ERROR_MESSAGE = '今月の残り録音回数の取得に失敗しました';

function isNullableNumber(value: unknown): value is number | null {
  return value === null || typeof value === 'number';
}

function parseAiUsageRemainingResponse(payload: unknown): AiUsageRemaining {
  if (!payload || typeof payload !== 'object') {
    throw new Error(AI_USAGE_FETCH_ERROR_MESSAGE);
  }

  const maybeEnvelope = payload as {
    success?: unknown;
    data?: {
      usageCount?: unknown;
      limit?: unknown;
      remaining?: unknown;
      creditMode?: unknown;
      nearestExpiresAt?: unknown;
    };
  };

  if (maybeEnvelope.success !== true) {
    throw new Error(AI_USAGE_FETCH_ERROR_MESSAGE);
  }

  const usageCount = maybeEnvelope.data?.usageCount;
  const limit = maybeEnvelope.data?.limit;
  const remaining = maybeEnvelope.data?.remaining;

  if (typeof usageCount !== 'number' || !isNullableNumber(limit) || !isNullableNumber(remaining)) {
    throw new Error(AI_USAGE_FETCH_ERROR_MESSAGE);
  }

  const nearestExpiresAt = maybeEnvelope.data?.nearestExpiresAt;
  return {
    usageCount,
    limit,
    remaining,
    creditMode: maybeEnvelope.data?.creditMode === 'ledger' ? 'ledger' : 'legacy',
    nearestExpiresAt: typeof nearestExpiresAt === 'string' ? nearestExpiresAt : null,
  };
}

export async function fetchAiUsageRemaining(): Promise<AiUsageRemaining> {
  const response = await fetch('/api/ai-usage/current', {
    method: 'GET',
    credentials: 'include',
  });

  const payload = await response.json().catch(() => null);

  if (!response.ok) {
    const { message } = readErrorEnvelope(payload);
    throw new Error(message ?? AI_USAGE_FETCH_ERROR_MESSAGE);
  }

  return parseAiUsageRemainingResponse(payload);
}

export function useInvalidateAiUsage(): () => void {
  const queryClient = useQueryClient();
  return useCallback(() => {
    void queryClient.invalidateQueries({
      queryKey: AI_USAGE_REMAINING_QUERY_KEY,
    });
  }, [queryClient]);
}

export function useAiUsageRemaining(): UseAiUsageRemainingResult {
  const { user } = useAuth();
  const organizationId = user?.currentOrganizationId ?? '';
  const hasOrganization = organizationId.length > 0;

  const query = useQuery({
    queryKey: [...AI_USAGE_REMAINING_QUERY_KEY, organizationId],
    queryFn: fetchAiUsageRemaining,
    staleTime: 30_000,
    enabled: hasOrganization,
  });

  if (!hasOrganization) {
    return {
      usageCount: null,
      limit: null,
      remaining: null,
      isUnlimited: false,
      creditMode: 'legacy',
      nearestExpiresAt: null,
      loading: false,
      isError: false,
      hasOrganization: false,
    };
  }

  return {
    usageCount: query.data?.usageCount ?? null,
    limit: query.data?.limit ?? null,
    remaining: query.data?.remaining ?? null,
    isUnlimited: query.data?.creditMode === 'ledger' ? query.data.remaining === null : query.data?.limit === null,
    creditMode: query.data?.creditMode ?? 'legacy',
    nearestExpiresAt: query.data?.nearestExpiresAt ?? null,
    loading: query.isLoading,
    isError: query.isError,
    hasOrganization: true,
  };
}
